import {
  getInformationObjectCreationDate,
  getInformationObjectCreationDateTimespan,
  getEntityName,
  findAndGetAATValue,
  getManifestObjects,
  getManifestUrls,
  getDateRange,
  getDisplayDate,
  IIIF_V2,
  IIIF_V3
} from "@thegetty-private/linked-data.js";

import * as localHumanMadeObjectHelpers from "@/helpers/HumanMadeObject";
import * as configHelpers from "@/helpers/configHelper";
import { getIndexedId, htmlSanitize } from "@thegetty-private/rad-app-utils";

import {
  getNameWithMarkdown,
  escapeMarkdownCharacters
} from "@/helpers/MarkdownHelpers";

import {
  getAssignedBy,
  getClassified,
  getClassifiedAs,
  getValueByClassification,
  getValuesByClassification,
  getValueOrContent,
  normalizeFieldToArray,
  getAttributedBy
} from "@thegetty/linkedart.js";
import aat from "@/data/aat";
import internal from "@/data/internal-ontologies";
import publicationRights from "@/data/publication-rights";

const CARRIED_OUT_BY = "carried_out_by";
const CHANGED_OWNERSHIP_THROUGH = "changed_ownership_through";
const CREATED_BY = "created_by";
const CLASSIFIED_AS = "classified_as";
const CUSTODY_TRANSFERRED_THROUGH = "custody_transferred_through";
const DEPICTS = "depicts";
const DIMENSION = "dimension";
const IDENTIFIED_BY = "identified_by";
const IS_ABOUT = "is_about";
const ABOUT = "about";
const LANGUAGE = "language";
const PART_OF = "part_of";
const PRODUCED_BY = "produced_by";
const REFERRED_TO_BY = "referred_to_by";
const REFERS_TO = "refers_to";
const SUBJECT_OF = "subject_of";
const ACCESS_POINT = "access_point";
const TRANSFERRED_CUSTODY_TO = "transferred_custody_to";
const TRANSFERRED_CUSTODY_FROM = "transferred_custody_from";
const TRANSFERRED_TITLE_FROM = "transferred_title_from";
const TRANSFERRED_TITLE_TO = "transferred_title_to";

/**
 * Takes a Collection InformationObject and gets the Series number
 *
 * @param {object} object - The JSON-LD for a Collection InformationObject
 * @return {number} the series number (the position within the object's direct parent)
 */
export function getInformationObjectOrder(object) {
  return getValueByClassification(
    normalizeFieldToArray(object, DIMENSION),
    aat.SEQUENCE
  );
}

/**
 * gets all the parts in the refers to
 *
 * @param {object} object  - the JSON-LD object
 *
 * @returns {array}
 */
export function getRefersTo(object) {
  const refers_to = [...normalizeFieldToArray(object, REFERS_TO)];
  if (!object || !refers_to.length) {
    return [];
  }

  refers_to.forEach((item) => {
    if (item.member_of) {
      refers_to.push(item.member_of);
    }
  });

  let parts = [];
  refers_to.forEach((item) => {
    if (!item) {
      return;
    }
    if (Array.isArray(item.part)) {
      item.part.forEach((part) => {
        parts.push(part);
      });
    }
    parts.push(item);
  });
  return parts;
}
/**
 * Takes a Collection InformationObject and gets the qualifier
 *
 * @param {object} object - The JSON-LD for a Collection InformationObject
 * @return {string} the InformationObject qualifier
 */
export function getInformationObjectQualifier(object) {
  const classifiedAs = normalizeFieldToArray(object, CLASSIFIED_AS);
  if (!classifiedAs.length) {
    return undefined;
  }
  if (
    classifiedAs.filter((item) => item.id === internal.ARCHIVAL_PART).length > 0
  ) {
    return internal.ARCHIVAL_PART_QUALIFIER;
  }

  if (classifiedAs.filter((item) => item.id === aat.REPOSITORY).length > 0) {
    return internal.REPOSITORY_QUALIFIER;
  }

  if (classifiedAs.filter((item) => item.id === aat.ARCHIVES).length > 0) {
    return internal.COLLECTION_QUALIFIER;
  }

  return undefined;
}

/**
 * Flattens the agent information into a unique map of agents & roles
 *
 * @param {object} agent - the agent
 * @param {object} map - the map of all agents
 * @param {object} extraRole - the extra role
 *
 */
function flattenAgent(agent, map, extraRole) {
  const cid = getIndexedId(agent.id);
  const existing = map[cid];
  if (Array.isArray(agent.assigned) == false) {
    agent.assigned = [];
  }
  if (extraRole) {
    agent.assigned.push(extraRole);
  }
  if (existing) {
    existing.assigned = existing.assigned.concat(agent.assigned);
  } else {
    map[cid] = agent;
  }
}

/**
 * Scans the full LOD Object and finds all of the relator codes and roles that we use
 *
 * @param {object} lodObject - the JSON-LD object
 *
 * @returns {object} - the map of roles
 */
export function buildAgentMap(lodObject) {
  let map = {};
  [...getCreatorsWithRoles(lodObject), ...getMetadataActors(lodObject)].forEach(
    (c) => {
      flattenAgent(c, map);
    }
  );

  getDepictedActors(lodObject).forEach((c) => {
    flattenAgent(c, map, { id: internal.DEPICTED, type: "Type" });
  });

  getAboutActors(lodObject).forEach((c) => {
    flattenAgent(c, map, { id: "subject", type: "Type" });
  });
  return map;
}
/**
 *
 * @param {object} object - a JSON-LD object to get both variations of is_about
 * @returns
 */
function getAbout(object) {
  if (object == undefined) {
    return [];
  }

  // try the new property
  let isAbout = normalizeFieldToArray(object, ABOUT);
  if (isAbout.length > 0) {
    return isAbout;
  }

  // fallback to old property
  return normalizeFieldToArray(object, IS_ABOUT);
}

/**
 * Takes a Collection InformationObject and gets LOs in is_about
 *
 * @param {object} object - The JSON-LD for a Collection InformationObject
 * @return {array} the array of LinguisticObject references
 */
export function getSubjectReferences(object) {
  let isAbout = getAbout(object);

  let linguisticObjects = isAbout.filter(
    (item) => item.type == "LinguisticObject"
  );
  let linguisticData = linguisticObjects;

  // dedupe the linguistic objects
  // can't use Set with just objects, so needed to stringify first, then put back
  linguisticObjects = Array.from(
    new Set(linguisticData.map(JSON.stringify))
  ).map(JSON.parse);

  return linguisticObjects;
}

/**
 * Takes an InformationObject and gets the abstract
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The abstract
 */
export function getAbstract(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    aat.ABSTRACT,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets the Other Descriptive Data
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} The other descriptive data
 */
export function getOtherDescriptiveData(object) {
  const referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  if (!referredToBy.length) {
    return [];
  }
  return getValuesByClassification(
    referredToBy,
    internal.OTHER_DESCRIPTIVE_DATA
  );
}

/**
 * Takes an InformationObject and gets the access restrictions
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The access restrictions
 */
export function getAccessRestrictions(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.ACCESS_RESTRICTIONS,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets the sensitive content note(s)
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 *
 * @return {string} The sensitive content notes
 */
export function getSensitiveContentNote(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    aat.SENSITIVE_CONTENT,
    separateValues
  );
}

/**
 * Takes an Information Object and gets the accession numbers
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} The array of accession number strings
 */
export function getAccessionNumbers(object) {
  const identifiedBy = normalizeFieldToArray(object, IDENTIFIED_BY);
  if (!identifiedBy.length) {
    return [];
  }
  return getValuesByClassification(identifiedBy, aat.ACCESSION_NUMBERS);
}

/**
 * Takes an Information Object and gets the Ref IDs
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} The array of Ref ID strings
 */
export function getRefIds(object) {
  const identifiedBy = normalizeFieldToArray(object, IDENTIFIED_BY);
  if (!identifiedBy.length) {
    return [];
  }
  return getValuesByClassification(identifiedBy, internal.REF_ID);
}

/**
 * Gets the actors in the depicted key of a HumanMadeObject embedded in an InformationObject
 *
 * @param {object} object - a JSON-LD object to get depicted people/groups from an embedded HumanMadeObject
 * @returns {array} - the array of Person/Group references
 */
export function getDepictedActors(object) {
  let depictedActors = [];
  const refers_to = getRefersTo(object);
  if (!object || !refers_to.length) {
    return depictedActors;
  }
  refers_to.forEach((hmo) => {
    let depicts = normalizeFieldToArray(hmo, DEPICTS);
    let actors = depicts.filter(
      (item) => item.type == "Person" || item.type == "Group"
    );
    depictedActors.push(...actors);
  });
  return depictedActors;
}

/**
 * Takes an InformationObject and gets the refs to all subject actors
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @deprecated
 * model with embedded HumanMadeObjects
 * @return {array} Array of objects references for People or Groups
 *
 * @todo - update to always use getDepictedActors once we move to the
 * new InformationObject model
 */
export function getAboutActors(object) {
  const isAbout = getAbout(object);

  if (!isAbout) {
    return [];
  }

  let actors = [];
  // this will never add anything to actors
  [...isAbout].forEach((act) => {
    if (act.type != "Person" && act.type != "Group") {
      return;
    }
    if (actors.findIndex((a) => getIndexedId(a.id) == getIndexedId(act.id))) {
      actors.push(act);
    }
  });

  return actors;
}

/**
 * Takes an InformationObject and gets the acquisition information
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The acquisition information
 */
export function getAcquisitionInformation(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.ACQUISITION_INFO,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets the link(s) to additional resources
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {array} existingLinks - the list of links previously pulled from getRelatedMedia (used to filter for dups)
 *
 * @return {array} The link(s) to additional resources
 */
export function getAdditionalResources(object, existingLinks = []) {
  const subjectOf = normalizeFieldToArray(object, SUBJECT_OF);
  if (!subjectOf.length) {
    return [];
  }

  // array of digital object classifications to display under additional resources
  const digitalClassifications = [
    internal.ADDITIONAL_RESOURCES,
    internal.MOVING_IMAGE
  ];

  const digitalObjects = getClassified(subjectOf, digitalClassifications, {
    classificationField: CLASSIFIED_AS,
    operator: "OR"
  });

  let links = [];

  let existingUrls = existingLinks.map((link) => {
    return link.href;
  });

  digitalObjects.forEach((item) => {
    const identifiedBy = normalizeFieldToArray(item, IDENTIFIED_BY);

    if (identifiedBy.length) {
      let url = item.access_point[0].id;
      // skip if the additional resource is already in the links
      if (existingUrls.includes(url)) {
        return;
      }
      links.push({
        name: getValueOrContent(identifiedBy[0]),
        href: url,
        iconName: "outbound14",
        withIcon: "after"
      });
    }
  });
  return links;
}

/**
 * Takes an InformationObject and gets the appraisal information
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The appraisal information
 */
export function getAppraisalInformation(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.APPRAISAL_INFO,
    separateValues
  );
}

/**
 * Constructs the basic metadata object
 *
 * @param {object} object  - the LOD Object
 * @param {array} primaryCreators - a list of primary creators
 * @param {function} translate - the translate function
 *
 * @returns {object}
 */
export function createBasicMetadataObject(object, primaryCreators, translate) {
  let title = getPageTitle(object, true, translate, false).markdown;

  let obj = {
    date_created: getDateCreated(object, translate),
    primary_creator: primaryCreators,
    component_id: getComponentId(object)
  };

  if (
    obj.date_created?.length != 1 ||
    title != new String(obj.date_created[0])
  ) {
    obj.title = title;
  }

  return obj;
}
/**
 * Takes a Collection InformationObject and gets the metadata fields
 *
 * @param {object} object - The JSON-LD for a Collection InformationObject
 * @param {array} actors - Array of person link objects (actors)
 * @param {array} language - Array of language aat value strings
 * @param {array} subjects - Array of subject linguistic objects
 * @param {array} aboutActors - Array of person link objects (collection is about)
 * @return {object} the metadata fields
 */
export function getArchivalPartMetadata({
  object,
  primaryCreators,
  otherCreators,
  language,
  subjects,
  aboutActors,
  translate
} = {}) {
  let obj = createBasicMetadataObject(object, primaryCreators, translate);
  obj["sensitive_content"] = getSensitiveContentNote(object);
  obj = { ...obj, ...getMetadataActorsByRole(otherCreators) };
  obj["component_scope_content"] = getScopeContentNotes(object);
  obj["box_folder"] = getBoxFolderLocation(object);
  obj["component_type"] = undefined;
  obj["access_restrictions"] = getAccessRestrictions(object);
  obj["physical_technical"] = getPhysicalTechnicalInformation(object);
  obj["subjects_topic"] = getSubjectsForDisplay(
    subjects,
    internal.SUBJECT_TOPIC
  );
  obj["subjects_place"] = getSubjectsForDisplay(
    subjects,
    internal.SUBJECT_PLACE
  );
  obj["subjects_temporal"] = getSubjectsForDisplay(
    subjects,
    internal.SUBJECT_TEMPORAL
  );
  obj["subjects_occupation"] = getSubjectsForDisplay(
    subjects,
    internal.SUBJECT_OCCUPATION
  );
  obj["subjects_uniform_title"] = getSubjectsForDisplay(
    subjects,
    internal.SUBJECT_UNIFORM_TITLE
  );
  obj["subjects_names"] = aboutActors;
  obj["genre_material"] = getSubjectsForDisplay(
    subjects,
    internal.GENRE_MATERIAL,
    "genre_material.name"
  );

  obj["abstract"] = getAbstract(object);
  obj["arrangement"] = getCollectionArrangement(object);
  obj["acquisition_info"] = getAcquisitionInformation(object);
  obj["related_material"] = getRelatedMaterials(object);
  // "FIXME" are these two language fields flipped;
  obj["language"] = getLanguageStatement(object);
  obj["language_statement"] = language;
  // revisit fields below to check if they are from old data or unneeded
  obj["preferred_citation"] = getPreferredCitation(object);
  obj["use_restrictions"] = getUseRestrictions(object);
  obj["separated_materials"] = getSeparatedMaterials(object);
  obj["extent"] = getExtentStatements(object);
  obj["ead_id"] = getEADID(object);
  obj["bibliography"] = getBibliographyNote(object);
  obj["biographical_history"] = getBiographicalHistory(object);
  obj["biographical"] = getBiographicalNote(object);
  obj["historical"] = getHistoricalNote(object);
  obj["other_descriptive_data"] = getOtherDescriptiveData(object);
  obj["physical_description"] = getPhysicalDescription(object);
  obj["physical_location"] = getPhysicalLocation(object);
  obj["dimensions"] = getDimensions(object);
  obj["physfacet"] = getPhysfacet(object);
  obj["rights_note"] = getRightsNote(object);
  obj["processing_information"] = getProcessingInformation(object);
  return obj;
}

/**
 * Takes an InformationObject and gets the box/folder information
 *
 * @param {object} object - The JSON-LD for InformationObject
 *
 * @return {string} The box/folder info
 *
 * @todo - make getArchivalContainerLabel default once we move to the
 * new InformationObject model
 */
export function getBoxFolderLocation(object) {
  return getArchivalContainerLabel(object);
}

/**
 * Takes an InformationObject and gets all of the box/folder information
 * from any embedded HumanMadeObject's set in the InformationObject
 *
 * @param {object} object - The JSON-LD for the InformationObject
 *
 * @return {string} The box/folder info
 */
export function getArchivalContainerLabel(object) {
  let boxAndFolders = [];
  const refers_to = getRefersTo(object);
  if (!object && !refers_to.length) {
    return undefined;
  }

  // add legacy support
  boxAndFolders = getReferredToByValueByClassification(
    object,
    internal.BOX_FOLDER_LOCATION
  );

  // if we have legacy data, show it, and _only_ it
  if (boxAndFolders.length) {
    return getAllValues(boxAndFolders).join(", ");
  }

  refers_to.forEach((hmo) => {
    let boxFolder = getReferredToByValueByClassification(
      hmo,
      internal.BOX_FOLDER_LOCATION
    );

    if (boxFolder) {
      if (Array.isArray(boxFolder)) {
        boxAndFolders = [...boxAndFolders, ...boxFolder];
      } else {
        boxAndFolders.push(boxFolder);
      }
    }
  });

  return boxAndFolders.length > 0
    ? getAllValues(boxAndFolders).join(", ")
    : undefined;
}

/**
 * Takes an InformationObject and gets the archivist(s)
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {string} The archivist(s)
 */
export function getArchivist(object) {
  let referredToBy;
  if (object && CREATED_BY in object) {
    referredToBy = normalizeFieldToArray(object[CREATED_BY], REFERRED_TO_BY);
  }
  if (!referredToBy) {
    return undefined;
  }
  let archivists = getValuesByClassification(
    referredToBy,
    internal.FINDING_AID_AUTHOR
  );

  return archivists;
}

/**
 * Takes an InformationObject and gets the biographical note
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The biographical history
 */
export function getBiographicalNote(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.BIOGRAPHICAL,
    separateValues
  );
}
/**
 * Takes an InformationObject and gets the historical note
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The biographical history
 */
export function getHistoricalNote(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.HISTORICAL,
    separateValues
  );
}
/**
 * Takes an InformationObject and gets the biographical history
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The biographical history
 */
export function getBiographicalHistory(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.BIOGRAPHICAL_HISTORY,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets the biographical history
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The custodial history
 */
export function getCustodialHistory(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.CUSTODIAL_HISTORY,
    separateValues
  );
}

/**
 * If unknown creator, adds localized display string
 *
 * @param {array} primaryCreator - array of creator objects
 * @param {function} translate - a VueI18n translate function
 *
 * @returns {array} creator objects
 */
export function getDisplayPrimaryCreator(primaryCreator, translate) {
  if (primaryCreator == undefined) {
    return [];
  }
  return primaryCreator.map((e) => ({
    ...e,
    primary_name:
      e.id === "person/UNKNOWN" ? translate("creator.unknown") : e.primary_name
  }));
}

/**
 * Takes an elasticSearch object and transforms it into the card fields
 *
 * @param {object} object - The elasticsearch object
 * @return {object} The card fields
 */
export function getCardFields(object, translatePart) {
  let primary_creator = getDisplayPrimaryCreator(
    object.primary_creator,
    translatePart
  ).map((e) => e["primary_name--markdown"]);

  if (object.entity_type === "collection") {
    return {
      creator_collector: primary_creator,
      parents: object.parentTree,
      collection_dates: object.date_created,
      physical_description: object.extent_statement,
      accession_number: object.accession_number
    };
  } else {
    return {
      creator_collector: primary_creator,
      dates: object.date_created,
      parents: object.parentTree
    };
  }
}

/**
 * Takes an elasticSearch object and transforms it into the cards fields secondary
 *
 * @param {object} object - The elasticsearch object
 * @return {object} The card fields secondary
 */
export function getCardFieldsSecondary(object) {
  if (object.entity_type === "collection") {
    return {
      brief_summary: object.abstract
    };
  } else {
    return {
      description: object.scope_content,
      box_folder: object.box_folder
    };
  }
}

/**
 * Takes a Collection InformationObject and gets the About tab fields
 *
 * @param {object} object - The JSON-LD for a Collection InformationObject
 * @param {function} translate - the VueI18n translate function
 * @param {function} dateTranslate - the VueI18n date translate function
 *
 * @return {object} the metadata fields
 */
export function getCollectionAbout(object, translate, dateTranslate) {
  let obj = {
    abstract: getAbstract(object),
    access_restrictions: getAccessRestrictions(object),
    publication_rights: undefined,
    biographical_history: getBiographicalHistory(object, true),
    biographical: getBiographicalNote(object),
    historical: getHistoricalNote(object),
    scope_content: getScopeContentNotes(object, true),
    arrangement: getCollectionArrangement(object),
    request_materials: undefined,
    related_material: getRelatedMaterials(object),
    separated_materials: getSeparatedMaterials(object),
    digital_collection: getDigitalCollection(object),
    digital_materials: getRelatedMediaLinks(object),
    // Administrative Information (preferred_citation, acquisition_info, processing_info, appraisal_info)
    preferred_citation: getPreferredCitation(object),
    acquisition_info: getAcquisitionInformation(object),
    accruals: getAccrualsInformation(object),
    custodial_history: getCustodialHistory(object),
    processing_information: getProcessingInformation(object),
    physical_description: getPhysicalDescription(object),
    physical_technical: getPhysicalTechnicalInformation(object),
    bibliography: getBibliographyNote(object),
    appraisal_info: getAppraisalInformation(object),
    archivist: getArchivist(object),
    revision_statement: getRevisionNotes(object, translate, dateTranslate),
    other_finding_aid: getOtherFindingAid(object),
    rights_note: getRightsNote(object)
  };
  return obj;
}

/**
 * Takes a Collection InformationObject and gets the Metadata tab fields
 *
 * @param {object} object - The JSON-LD for a Collection InformationObject
 * @param {array} actors - Array of person link objects (actors)
 * @param {array} primaryCreator - Array of person link objects (primary creator(s))
 * @param {array} aboutActors - Array of person link objects (collection is about)
 * @param {array} subjects - Array of subject linguistic objects
 * @param {array} language - Array of language AAT string values
 * @return {object} the metadata fields
 */
export function getCollectionMetadata({
  object,
  primaryCreator = [],
  otherCreators = [],
  aboutActors = [],
  subjects = [],
  language,
  translate
} = {}) {
  let obj = createBasicMetadataObject(object, primaryCreator, translate);

  obj["sensitive_content"] = getSensitiveContentNote(object);

  // if using new IO model, add non-primary creators here
  obj = { ...obj, ...getMetadataActorsByRole(otherCreators) };

  obj["accession_number"] = getAccessionNumbers(object);
  obj["extent"] = getExtentStatements(object);
  obj["subjects_topic"] = getSubjectsForDisplay(
    subjects,
    internal.SUBJECT_TOPIC
  );
  obj["subjects_place"] = getSubjectsForDisplay(
    subjects,
    internal.SUBJECT_PLACE
  );
  obj["subjects_temporal"] = getSubjectsForDisplay(
    subjects,
    internal.SUBJECT_TEMPORAL
  );
  obj["subjects_occupation"] = getSubjectsForDisplay(
    subjects,
    internal.SUBJECT_OCCUPATION
  );
  obj["subjects_uniform_title"] = getSubjectsForDisplay(
    subjects,
    internal.SUBJECT_UNIFORM_TITLE
  );
  obj["subjects_names"] = aboutActors;
  obj["genre_material"] = getSubjectsForDisplay(
    subjects,
    internal.GENRE_MATERIAL,
    "genre_material.name"
  );

  obj["language"] = language;
  obj["language_statement"] = getLanguageStatement(object);

  return obj;
}

/**
 * Takes an ArchivalPart's Collection and gets the relevant metadata to display
 *
 * @param {object} object - The JSON-LD for Collection
 * @param {object} collectionLink - the link to the collection
 * @param {boolean} onlyCollectionLink - return only the collection link
 *
 * @return {object} The metadata fields
 */
export function getCollectionMetadataForArchivalPart(
  object,
  collectionLink,
  onlyCollectionLink = false
) {
  if (onlyCollectionLink) {
    return { collection_name: collectionLink };
  }
  return {
    collection_name: collectionLink,
    arrangement: getCollectionArrangement(object),
    accession_number: getAccessionNumbers(object)
  };
}

/**
 * Takes an InformationObject and gets the scope of the collection
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The collection scope
 */
export function getScopeContentNotes(object, separateValues = false) {
  let scopeNote = getReferredToByValueByClassification(
    object,
    internal.SCOPE_AND_CONTENT,
    separateValues
  );
  return scopeNote;
}

/**
 * Gets the multi-part note parts
 *
 * @param {object} object - the JSON-LD object
 * @param {string} type  - the type to filter by
 *
 * @returns {array} - the list of note parts (with format and content)
 */
function getMultiPartNote(object, type) {
  const referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  let multiNote = [];
  for (const part of getClassifiedAs(referredToBy, type)) {
    if (part["part"]) {
      part["part"].forEach(
        (note) => (multiNote = multiNote.concat(getNoteParts(note)))
      );
    }
  }
  // filter on values that aren't '' or undefined
  multiNote = multiNote.filter((element) => {
    let value = getValueOrContent(element);
    if (
      value == undefined ||
      (typeof value === "string" && value.trim() == "")
    ) {
      return false;
    }
    return true;
  });
  return multiNote;
}

/**
 * Recursively iterates through the parts of a multi-part note to determine if they have a content value
 * or contain nested parts of their own
 *
 * @param {note} object - The JSON-LD object representing the mult-part scope note
 *
 * @return {array} The content of the note or the parts of the ordered list
 */
function getNoteParts(note) {
  let subParts = [];
  if (note["part"] == undefined) {
    const value = getValueOrContent(note);
    if (value == undefined || value.trim() == "") {
      return [];
    }
    return [note];
  }
  note["part"].forEach(
    (part) =>
      // add spaces for markdown line break formatting
      (subParts = subParts.concat(getNoteParts(part)))
  );
  return subParts;
}

/**
 * Takes an InformationObject and gets the arrangement of the collection
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The collection arrangement
 */
export function getCollectionArrangement(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.ARRANGEMENT,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets the use restrictions of the component
 *
 * @param {object} object - The JSON-LD for InformationObject
 *
 * @return {string} The component use restrictions
 */
export function getUseRestrictions(object) {
  return getReferredToByValuesByClassification(
    object,
    internal.PUBLICATION_RIGHTS
  );
}

/**
 * Takes an InformationObject and gets the component id
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {string} The component id
 */
export function getComponentId(object) {
  return getValueByClassification(
    normalizeFieldToArray(object, IDENTIFIED_BY),
    internal.COMPONENT_ID
  );
}

/**
 * Takes an InformationObject and gets the component type
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {string} The component type
 */
export function getComponentType(object) {
  const componentTypes = [
    internal.ARCHIVAL_FILE,
    internal.ARCHIVAL_ITEM,
    internal.ARCHIVAL_OTHERLEVEL,
    internal.ARCHIVAL_SERIES,
    internal.ARCHIVAL_SUBSERIES
  ];
  const classifiedAs = normalizeFieldToArray(object, CLASSIFIED_AS);

  if (!classifiedAs) {
    return undefined;
  }

  // check if classified_as contains one of the three possible component types
  const componentType = classifiedAs.filter((item) => {
    return componentTypes.find((type) => item.id == type);
  });

  if (componentType.length) {
    const type = componentType[0]._label;

    if (type === "archival file") {
      return "component_type.archival_file";
    } else if (type === "archival item") {
      return "component_type.archival_item";
    } else if (type === "archival otherlevel") {
      return "component_type.archival_other";
    } else if (type === "archival series") {
      return "component_type.archival_series";
    } else if (type === "archival subseries") {
      return "component_type.archival_subseries";
    }
  }
}

/**
 * Wrapper specifying which activities to parse for actors with roles
 * to display with collection and component metadata
 *
 * @param {object} object - The JSON-LD for InformationObject
 *
 * @returns {array} array of actors
 */
export function getMetadataActors(object) {
  let actors = [
    ...getReferredToActorsWithRoles(object),
    ...getOwnersWithRoles(object),
    ...getPreviousOwnersWithRoles(object),
    ...getCustodiansWithRoles(object),
    ...getPreviousCustodiansWithRoles(object)
  ];

  return actors;
}

/**
 * Takes an InformationObject and gets the creators/collectors
 * with their roles from an embedded HumanMadeObject in the
 * InformationObject
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} The creators/collectors of the collection
 */
export function getCreatorsWithRoles(object) {
  return [
    ...getReferredToActorsWithRoles(object, PRODUCED_BY, CARRIED_OUT_BY),
    ...getReferredToActorsWithRoles(object, "created_by", CARRIED_OUT_BY)
  ];
}

/**
 * Takes an InformationObject and gets the owners with their
 * roles from an embedded HumanMadeObject in the InformationObject
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} The owners of the collection
 */
export function getOwnersWithRoles(object) {
  return getReferredToActorsWithRoles(
    object,
    CHANGED_OWNERSHIP_THROUGH,
    TRANSFERRED_TITLE_TO
  );
}

/**
 * Takes an InformationObject and gets the previous owners with their
 * roles from an embedded HumanMadeObject in the InformationObject
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} The previous owners of the collection
 */
export function getPreviousOwnersWithRoles(object) {
  return getReferredToActorsWithRoles(
    object,
    CHANGED_OWNERSHIP_THROUGH,
    TRANSFERRED_TITLE_FROM
  );
}

/**
 * Takes an InformationObject and gets the custodians with their
 * roles from an embedded HumanMadeObject in the InformationObject
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} The custodians of the collection
 */
export function getCustodiansWithRoles(object) {
  return getReferredToActorsWithRoles(
    object,
    CUSTODY_TRANSFERRED_THROUGH,
    TRANSFERRED_CUSTODY_TO
  );
}

/**
 * Takes an InformationObject and gets the previous custodians with their
 * roles from an embedded HumanMadeObject in the InformationObject
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} The previous custodians of the collection
 */
export function getPreviousCustodiansWithRoles(object) {
  return getReferredToActorsWithRoles(
    object,
    CUSTODY_TRANSFERRED_THROUGH,
    TRANSFERRED_CUSTODY_FROM
  );
}

/**
 * Takes a InformationObject and gets the primary creator/collector
 *
 * @param {object} informationObject - information object LOD
 * @param {array} creators - array of creator LOD objects
 * @return {array} - An array of creator LOD or creator objects
 */
export function getPrimaryCreators(informationObject, creators = []) {
  let primaryCreators = getCreatorsWithRoles(informationObject).filter((c) => {
    return c.assigned
      .map((role) => role.id)
      .some((val) => val == aat.PREFERRED_TERMS);
  });

  let matched = [];
  // return creator LOD if available, filtering out duplicates
  if (primaryCreators.length && creators.length) {
    let seen = [];
    for (let pc of primaryCreators) {
      let pcid = getIndexedId(pc.id);
      let creator = creators.filter((c) => getIndexedId(c.id) == pcid)[0];
      if (seen.includes(pcid)) {
        continue;
      }
      seen.push(pcid);
      // if this is the primary creator, do we need the role?
      creator.assigned = pc.assigned;
      matched.push(creator);
    }
    return matched;
  }
  return primaryCreators;
}

/**
 * Takes an InformationObject and gets the creation date(s)
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {function} translate - the translate function
 *
 * @return {string|array} The creation date(s)
 */
export function getDateCreated(object, translate) {
  if (!object) {
    return [];
  }

  // Check to see if we 're using the new InformationObject model with
  // embedded HumanMadeObjects
  const refers_to = getRefersTo(object);

  // If we have refers_to, pass those objects through the function to build
  // the date
  if (refers_to.length) {
    return refers_to.reduce((result, hmo) => {
      let dates = result.concat(getDateCreated(hmo, translate));
      // dedup

      return Array.from(new Set(dates));
    }, []);
  }

  let dates = getInformationObjectCreationDate(object);
  if (object[PRODUCED_BY]) {
    const createdDate = localHumanMadeObjectHelpers.getDateCreated(object);
    dates = [...createdDate];

    /*
     I  _think_ I misunderstood Ben when we were talking about computed vs. inferred dates.
     Computed dates are just dates where a date string is provided but it was parsed and computed into a value.
     inferred dates are when a HMO is given a date because the model requires it. but none was provided.
     This flips the logic to remove the logic around computed dates and just filters inferred dates.
    */

    // if we were to combine these, we'd append the bulk to the last entry in dates above as long as it wasn't already included
    const timespan = object?.produced_by?.timespan;
    if (getClassified(timespan, internal.INFERRED_DATE_RANGE).length == 0) {
      (timespan?.part || []).forEach((part) => {
        if (
          getClassifiedAs(part, aat.BULK_DATES).length > 0 &&
          // also had a test for computed dates
          getClassified(part, internal.INFERRED_DATE_RANGE).length == 0 &&
          part?.identified_by?.length > 0
        ) {
          const value = getValueOrContent(part?.identified_by[0]);

          // skip if creation date string includes bulk date
          if (
            createdDate.some((date) => {
              return date.includes(value);
            })
          ) {
            return;
          }

          if (value.includes("bulk")) {
            dates.push(value);
          } else {
            dates.push(
              translate("collection.bulk_date", {
                bulk: value
              })
            );
          }
        }
      });
    }
  }

  if (Array.isArray(dates)) {
    dates = dates.map((date) =>
      localHumanMadeObjectHelpers.trimAndStripCommas(date)
    );
  } else {
    dates = localHumanMadeObjectHelpers.trimAndStripCommas(dates);
  }
  // dedup
  return Array.from(new Set(dates));
}

/**
 * Takes an InformationObject and gets the creation timespan which has the
 * human readable and programmatic representation of the timespan
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} An array of timespan objects
 */
export function getDateCreatedAsTimespan(object) {
  if (!object) {
    return [];
  }
  // I _think_ wwe need to manually get the production data, and the look at ALL the parts
  let results = getInformationObjectCreationDateTimespan(object);
  return results.filter(
    // also had a test for computed dates
    (result) => getClassified(result, internal.INFERRED_DATE_RANGE).length == 0
  );
}

/**
 * Takes an InformationObject and gets the digital collection information/link
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The digital collection information/link
 */
export function getDigitalCollection(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.DIGITAL_COLLECTION,
    separateValues
  );
}

/**
 * Takes an InformationObject Elasticsearch object and gets the number of digitized items/children
 * @todo @legacy may remove when digitized children field is deprecated
 * @param {object} object - The InformationObject Elasticsearch object
 * @return {string} The number of digitized items/children
 */
export function getDigitizedItems(object) {
  if (!object.digitized_children || object.digitized_children == 0) {
    return undefined;
  }

  return object.digitized_children;
}

/**
 * Takes an InformationObject Elasticsearch object and returns whether or not self or descendants
 * have associated digitized items
 *
 * @param {object} object - The InformationObject Elasticsearch object
 * @return {boolean} whether or not there are associated digitized items
 */
export function getHasDigitizedChildren(object) {
  if (!object.contains_digital_material) {
    return false;
  }

  return object.contains_digital_material;
}

/**
 * Wrapper for invoking both digitized children helpers
 * @todo @legacy may remove digitized items reference when digitized children is deprecated
 * @param {Object} object - the ElasticSearch object to transform
 * @returns {Boolean} whether or not there are digitized children
 */
export function hasDigitizedChildren(object) {
  return getDigitizedItems(object) || getHasDigitizedChildren(object)
    ? true
    : false;
}

/**
 * Takes an InformationObject and gets the dimensions
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The dimensions
 */
export function getDimensions(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.DIMENSIONS,
    separateValues
  );
}

/**
 * Takes an Information Object and gets the EAD ID
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} The array of EAD ID strings
 */
export function getEADID(object) {
  const identifiedBy = normalizeFieldToArray(object, IDENTIFIED_BY);
  if (!identifiedBy.length) {
    return [];
  }
  let vals = getValuesByClassification(identifiedBy, internal.EAD_ID);
  if (vals && vals.length != 0) {
    return vals;
  }
  return getValuesByClassification(identifiedBy, internal.EAD_ID_ALT);
}

/**
 * Takes an InformationObject and gets the bibliography note
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The bibliography note(s)
 */
export function getBibliographyNote(object, separateValues = false) {
  let val = getReferredToByValueByClassification(
    object,
    internal.BIBLIOGRAPHY,
    separateValues
  );
  if (val == undefined) {
    return undefined;
  }

  return val;
}

/**
 * Takes an InformationObject and gets the physical description note
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The physical description note(s)
 */
export function getPhysicalDescription(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.PHYSICAL_DESCRIPTION,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets the extents
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {string} The extents
 */
export function getExtentStatements(object) {
  return getReferredToByValuesByClassification(
    object,
    internal.EXTENT_STATEMENT
  );
}

/**
 * Takes an InformationObject and gets the related materials
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {function} translate - the VueI18n translate function
 * @param {function} dateTranslate - the VueI18n date translate function
 *
 * @return {array} Array of related materials strings
 */
export function getRevisionNotes(object, translate, dateTranslate) {
  const referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  const result = getClassifiedAs(referredToBy, internal.REVISION_STATEMENT);

  let toReturn = [];
  result.forEach((state) => {
    let date = translate("collection.revision_unknown_date");
    if (
      state.created_by &&
      state.created_by.timespan &&
      state.created_by.timespan.begin_of_the_begin
    ) {
      date = new Date(state.created_by.timespan.begin_of_the_begin);
    }

    toReturn.push(
      translate("collection.revision_statement", {
        date: dateTranslate(date),
        statement: state.content
      })
    );
  });
  return toReturn;
}

/**
 * Takes an InformationObject and gets the accruals information
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {array} The accruals information
 */
export function getAccrualsInformation(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.ACCRUALS,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets other finding aid information
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {array} other finding aid information
 */
export function getOtherFindingAid(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.OTHER_FINDING_AID,
    separateValues
  );
}

/**
 * gets all of the AAT language references from the Object
 *
 * @param {object} object
 *
 * @returns {array}
 */
export function getLanguageReferences(object) {
  let languages = [];
  const refers_to = getRefersTo(object);
  refers_to.forEach((hmo) => {
    languages.push(...normalizeFieldToArray(hmo, LANGUAGE));
  });

  return languages;
}

/**
 * Gets the aat values for languages of an Information Object
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {array} aatQualifiers - an array of AAT JSON-LD Documents to reference
 * @return {array} Array of string languages
 *
 * @todo - update to always get languages inside the HMO once we move to the
 * new InformationObject model
 */
export function getLanguage(object, aatQualifiers, lang_) {
  let result = [];
  let languages = getLanguageReferences(object);

  languages.forEach((item) => {
    let aatValue = findAndGetAATValue({
      id: item.id,
      qualifiers: aatQualifiers,
      lang: lang_
    });
    if (aatValue) {
      result.push(aatValue);
    }
  });
  return result;
}

/**
 * Takes an InformationObject and gets the language statement
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The language statement
 */
export function getLanguageStatement(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    aat.MATERIAL_STATEMENT,
    separateValues
  );
}

/**
 * getName
 * @param {object} object -- the Object to look in for the name of the InformationObject
 *
 * @returns {string} the title
 **/
export function getName(object) {
  let name = getEntityName(object);
  return htmlSanitize(name, { allowedTags: [] });
}

/**
 * Gets the page title for a collection or component
 *
 * @param {object} object -- the Object to look in for the InformationObject page
 * @param {boolean} returnObject -- whether to return an object or a string
 * @param {function} translate -- the translate function
 * @param {boolean} withComponentId -- whether to include the component id or not
 *
 * @returns {string} the title
 **/
export function getPageTitle(
  object,
  returnObject = false,
  translate,
  withComponentId = true
) {
  if (object == undefined) {
    return "Untitled";
  }

  let text = getName(object);
  if (returnObject == false) {
    return formatTitleAndDate(text, getDateCreated(object, translate));
  }
  let markdown = getNameWithMarkdown(object);
  if (markdown == undefined) {
    markdown = escapeMarkdownCharacters(text);
  }

  let componentId = null;
  if (withComponentId) {
    componentId = getComponentId(object);
  }

  return {
    text: formatTitleAndDate(
      text,
      getDateCreated(object, translate),
      componentId
    ),
    markdown: formatTitleAndDate(
      markdown,
      getDateCreated(object, translate),
      componentId
    )
  };
}

// is this needed?
export function getParentID(object) {
  const partOf = normalizeFieldToArray(object, PART_OF);
  if (!partOf.length) {
    return undefined;
  }
  return partOf[0].id;
}

/**
 * Takes an InformationObject and gets the physfacet
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The physfacet
 */
export function getPhysfacet(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.PHYSFACET,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets the Rights note
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The rights note
 */
export function getRightsNote(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.RIGHTS_NOTE,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets the physical location
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The physical location
 */
export function getPhysicalLocation(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.PHYSICAL_LOCATION,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets the preferred citation
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The preferred citation
 */
export function getPreferredCitation(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.PREFERRED_CITATION,
    separateValues
  );
}

/**
 * Takes an InformationObject and gets the collection processing date range
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {object} The processing date range ex: { gte:"2010-08-01", lte:"2011-06-01" }
 */
export function getProcessingDateRange(object) {
  // should we check that the created_by object has specific classification?
  if (!object || !object[CREATED_BY]) {
    return undefined;
  }
  return getDateRange(object[CREATED_BY]);
}

/**
 * Takes a date range object and returns a formatted date range
 *
 * @param {object} dateObject - The processing date range ex: { gte:"2010-08-01", lte:"2011-06-01" }
 * @return {string} The processing date range ex: "August 1, 2010 - June 1, 2011"
 */
export function getProcessingDateRangeFormatted(dateObject) {
  const dateRange = getProcessingDateRange(dateObject);
  if (!dateRange) {
    return undefined;
  }

  return getDisplayDate(dateRange);
}

/**
 * Takes an InformationObject and gets the processing information
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @return {string} The processing information
 */
export function getProcessingInformation(object, separateValues = false) {
  return getReferredToByValueByClassification(
    object,
    internal.PROCESSING_INFORMATION,
    separateValues
  );
}

/**
 * Takes a collection object and returns the publication rights statement
 *
 * @param {object} collection - The collection object
 * @param {function} translate - the vueI18n translate function
 *
 * @return {string} The publication rights statement
 */
export function getPublicationRights(collection, translate) {
  let rightsStatement =
    publicationRights.specialCollection.publication_rights_key;

  if (collection == undefined) {
    return undefined;
  }

  let access_policy = getAccessPolicy(collection, "publication_rights_policy");

  if (isInstitutionalCollection(collection)) {
    rightsStatement =
      publicationRights.institutionalCollection.publication_rights_key;
  }
  let string = translate(rightsStatement, {
    link: access_policy
  });

  return string;
}

/**
 * Gets all values from the JSON-LD structure
 *
 * @param {array} values - an array of objects
 *
 * @returns {array} - an array of strings or primitives
 */
export function getAllValues(incomingValues) {
  if (incomingValues == undefined) {
    return [];
  }

  // make sure we're not modifying the raw data
  let values = JSON.parse(JSON.stringify(incomingValues));
  values = values.map((value) => {
    return getValueOrContent(value);
  });
  // remove nulls
  return values.filter((value) => value);
}

/**
 * Local Helper
 * Returns the first value that matches the requestedClassification under the referred_to_by array
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {string} requestedClassification - The aat or data.getty classification ID to match
 * @param {boolean} returnContentStructure - whether to expose the content structure or not
 *
 * @return {string|array}
 */
function getReferredToByValueByClassification(
  object,
  requestedClassification,
  returnContentStructure = false
) {
  // Each piece will need to rendered and treated separately
  // this https://data.getty.edu/research/collections/component/a9ab6b23-4422-5ff6-a201-18abd892c18d/notes/3 is a good example

  // assumption... this method returns an array of objects with clearly described formats... either text/html or text/markdown
  // it then leaves it to the UX to choose how to render that

  // because of that... we shouldn't have to care about escaping markdown

  // if we have no parts, this should be just the top-level classification entries
  let referred_to_by = getClassifiedAs(
    normalizeFieldToArray(object, REFERRED_TO_BY),
    requestedClassification
  );

  let isMultiPart = false;
  // if we do have parts, this should get all of the parts
  if (getAllValues(referred_to_by).length == 0) {
    // skip out if we have nothing
    referred_to_by = getMultiPartNote(object, requestedClassification);
    isMultiPart = true;
  }

  let values = [];
  // for each linguistic object, grab the content
  referred_to_by.forEach((description) => {
    let content = getValueOrContent(description);
    if (content == undefined || content.trim() == "") {
      return;
    }

    let value = {
      content: content,
      contentType: description.format
    };

    // return any additional classifications but don't return the classification requested
    let extraClassifications = normalizeFieldToArray(
      description,
      CLASSIFIED_AS
    );
    extraClassifications = extraClassifications.filter(
      (e) => e.id != requestedClassification
    );

    if (extraClassifications.length) {
      value.classified_as = extraClassifications;
    }

    values.push(value);
  });

  // trim trailing and leading newlines
  values = values.map((value) => {
    // trim leading and trailing spaces
    value.content = value.content.trim().replace(/^(\n\r?|<br>|\\s)+/, "");
    value.content = value.content.trim().replace(/(\n\r?|<br>|\\s)+$/, "");

    return value;
  });

  if (returnContentStructure == false) {
    return values;
  }

  // if we returnContentStructure, then we want to expose the content structure back
  const isMultipleNotes = isMultiPart == false && values.length > 1;
  return { values, isMultiPart, isMultipleNotes };
}

/**
 * Local Helper
 * Returns an array of all values that match the requestedClassification under the referred_to_by array
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {string} requestedClassification - The aat or data.getty classification ID to match
 * @return {array} All matching values (generally strings)
 */
function getReferredToByValuesByClassification(
  object,
  requestedClassification
) {
  const referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  const result = getValuesByClassification(
    referredToBy,
    requestedClassification
  );
  if (!result) {
    return [];
  }
  return result;
}

/**
 * Takes an Information Object and gets the repository number(s)
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} The array of repository number string(s)
 */
export function getRepositoryNumbers(object) {
  const identifiedBy = normalizeFieldToArray(object, IDENTIFIED_BY);
  if (!identifiedBy.length) {
    return [];
  }
  return getValuesByClassification(identifiedBy, aat.REPOSITORY_NUMBERS);
}

/**
 * Takes an InformationObject and gets the related materials
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} Array of related materials strings
 */
export function getRelatedMaterials(object) {
  const relatedMaterials = getReferredToByValuesByClassification(
    object,
    internal.RELATED_MATERIALS
  );

  return relatedMaterials;
}

/**
 * Gets the Access Policy
 * @param {object} collection - The collection JSON-LD object
 * @param {string} key - Which policy link to get
 * @returns
 */
export function getAccessPolicy(collection, key = "access_policy") {
  if (isInstitutionalCollection(collection)) {
    return publicationRights.institutionalCollection[key];
  }
  return publicationRights.specialCollection[key];
}

/**
 * Takes a collection object and returns the request material statement
 *
 * @param {object} collection - The collection JSON-LD object
 * @param {function} translate - translate function to have access to locales
 *
 * @return {string} The request material statement
 */
export function getRequestMaterial(collection, translate) {
  const localeKey = "field_statements.request_materials";
  let catalogRecord = getCatalogRecord(collection);

  const params = {
    catalog: catalogRecord,
    link: catalogRecord
  };
  params.access_policy = getAccessPolicy(collection);
  // if we don't have a catalog record ...
  if (!catalogRecord) {
    return translate("field_statements.default_publication_rights", {
      link: getAccessPolicy(collection)
    });
  }

  return translate(localeKey, params);
}

/**
 * Takes an InformationObject and gets the separated materials
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @return {array} Array of separated materials strings
 */
export function getSeparatedMaterials(object) {
  const separatedMaterials = getReferredToByValuesByClassification(
    object,
    internal.SEPARATED_MATERIALS
  );

  return separatedMaterials;
}

/**
 * Takes Subjects Linguistic Objects and gets the subjects of the classification specified for display
 *
 * @param {array} subjects - The array of Subjects Linguistic Objects
 * @param {string} classification - The string of the internal classification requested
 * @return {object} Array of objects with subject display names and ids
 */
export function getSubjectFields(subjects, classification) {
  let classified = [];
  if (subjects == undefined) {
    return classified;
  }
  for (let subject of subjects) {
    const term = getValueOrContent(subject);

    let oldModel = true;
    // the new model uses an 'attributed/assigned' classification
    for (let cls of normalizeFieldToArray(subject, CLASSIFIED_AS)) {
      if (cls.attributed_by) {
        oldModel = false;
        break;
      }
    }

    normalizeFieldToArray(subject, CLASSIFIED_AS).filter((cls) => {
      const attributed = getAttributedBy(cls, "classified_as");
      if (
        cls.id == classification &&
        (oldModel ||
          (oldModel == false &&
            attributed.length > 0 &&
            attributed[0]?.id == aat.PREFERRED_TERMS))
      ) {
        classified.push({
          name: term,
          id: getIndexedId(subject.id)
        });
      }
    });
  }

  // sort alphabetically
  classified.sort((a, b) => (a.name > b.name ? 1 : -1));

  return classified;
}

/**
 * Takes Subjects Linguistic Objects and gets the subjects of the classification specified for display
 *
 * @param {array} subjects - The array of Subjects Linguistic Objects
 * @param {string} classification - The string of the internal classification requested
 * @param {string} fieldName - the field name to search
 * @return {object} Array of objects with subject display names and params for links for routing
 */
export function getSubjectsForDisplay(
  subjects,
  classification,
  fieldName = "subjects.name"
) {
  return getSubjectFields(subjects, classification).map((value) => {
    return {
      name: value.name,
      to: { name: "search", query: { [fieldName]: value.name } }
    };
  });
}

/**
 * Takes Subjects Linguistic Objects and gets the subject objects of the classification specified to be indexed
 *
 * @param {array} subjects - The array of Subjects Linguistic Objects
 * @param {string} classification - The string of the internal classification requested
 * @return {array} Array of place subject objects
 */
export function getSubjectFieldForIndexer(subjects, classification) {
  return getSubjectFields(subjects, classification).map((value) => {
    return {
      id: value.id,
      name: value.name,
      "name--text": value.name
    };
  });
}

/**
 * Returns technical data component metadata fields object
 * @param {Object} object
 * @param {Object} translate translate function
 * @param {Boolean} includeIiif whether to include iiif fields
 * @param {Array} UANs array of UAN identifiers (for JPCA image identifiers)
 * @param {String} lang ISO language code
 *
 * @returns {object} technicalMetadataFields
 */
export function getTechnicalMetadataFields(
  object,
  translate = undefined,
  includeIiif = false,
  UANs = [],
  lang = "en"
) {
  if (object == undefined) {
    return;
  }
  const out = {};

  const indexedId = object._id ? object._id : object.id;

  if (indexedId) {
    out["indexed_id"] = indexedId;
  }

  // get and format other ids metadata
  const otherIds = getOtherIds(object, translate);
  if (Object.keys(otherIds).length > 0) {
    out["other_ids"] = otherIds;
  }

  // get and format iiif metadata
  let url = getManifestUrl(object);
  if (includeIiif && url) {
    let manifests = getManifestObjects(object, {
      excludeCustom: false
    });

    if (manifests.length > 1) {
      manifests.forEach((m) => {
        if (m.format == IIIF_V3) {
          m.title = translate("m-technical-data.manifest_url_v3");
        }
        if (m.format == IIIF_V2) {
          m.title = translate("m-technical-data.manifest_url_v2");
        }
      });
    }

    let iiif = {
      manifests: manifests,
      viewer_url: configHelpers.getMiradorUrl() + "?manifest=" + url
    };

    out["iiif"] = iiif;

    // add UANs to technical metadata for JPC
    if (UANs.length) {
      out["other_ids"] = out["other_ids"] ?? {};
      out["other_ids"].uan_id = {
        label: "uan_id",
        values: UANs
      };
    }
  }

  // get and format last updated date
  // date format options
  const options = { year: "numeric", month: "long", day: "numeric" };
  let date = new Date(object.last_updated);
  let lastUpdated = date == "Invalid Date" ? undefined : date;
  if (lastUpdated) {
    out["last_updated"] = new Intl.DateTimeFormat(lang, options).format(date);
  }

  // get and format sparqlUrl
  const sparqlUrl = getSparqlUIUrl(indexedId);
  if (sparqlUrl) {
    out["sparql_api"] = sparqlUrl;
  }

  // get and format idManagerUrl
  const idManagerUrl = configHelpers.getIdManagerLinksUrl(indexedId);
  if (idManagerUrl) {
    out["id_relationships"] = idManagerUrl;
  }

  return out;
}

/**
 * returns additional internal ids (DOR ID, TMS ID)
 *
 * @param {Object} object
 * @returns  {array} array of other ids object
 */
export function getOtherIds(
  object,
  translate,
  baseLocaleKey = "m-technical-data"
) {
  if (object == undefined) {
    return;
  }

  let otherIds = {};

  let accessionNum = getAccessionNumbers(object)[0];
  if (accessionNum) {
    otherIds["accession_number"] = accessionNum;
  }

  let refId = getRefIds(object)[0];
  if (refId) {
    otherIds["ref_id"] = refId;
  }

  let otherIdObjects = {};
  Object.keys(otherIds).forEach((k) => {
    otherIdObjects[k] = {
      name: String(otherIds[k]),
      label: translate ? translate(`${baseLocaleKey}.${k}`) : k,
      text: translate ? translate(`${baseLocaleKey}.${k}_text`) : k
    };
  });

  return otherIdObjects;
}

/**
 * Returns returns example sparql ui query based on object
 * @param {Object} indexedId
 *
 * @returns {String} sparql ui endpoint with example query url
 */
export function getSparqlUIUrl(indexedId) {
  const endpoint = configHelpers.getDataUrl(
    "https://data.getty.edu/research/collections/"
  );
  let query = `PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
  PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
  PREFIX crm: <http://www.cidoc-crm.org/cidoc-crm/>
  PREFIX skos: <http://www.w3.org/2004/02/skos/core#>
  PREFIX la: <https://linked.art/ns/terms/>
  PREFIX aat: <http://vocab.getty.edu/aat/>
  PREFIX getty: <http://data.getty.edu/local/>

  SELECT * WHERE {
    <${indexedId}> ?pred ?obj
  }`;

  query = encodeURIComponent(query);
  return `${endpoint}sparql-ui#query=${query}`;
}

/**
 * Gets the manifest URL from the object
 * @param {object} object - the JSON-LD Object
 *
 * @returns {string} the manifest URL
 */
export function getManifestUrl(object) {
  let url = getManifestUrls(object)[0];

  if (!url) {
    return undefined;
  }

  return url;
}

/**
 * Formats the title and date for an Archival Part to be displayed throughout the UI
 *
 * @param {String} title
 * @param {Array|String} dates
 * @param {String} component id
 *
 * @returns {String}
 */
export function formatTitleAndDate(title, dates, componentId = undefined) {
  if (title == undefined) {
    return undefined;
  }

  // new logic is to concatenate all dates with display language
  let title_ = localHumanMadeObjectHelpers.trimAndStripCommas(title);
  const componentIdPart = componentId
    ? localHumanMadeObjectHelpers.trimAndStripCommas(componentId) + " "
    : "";
  let datePart = "";
  if (dates != undefined) {
    if (Array.isArray(dates) == false) {
      dates = [dates];
    }
    dates.forEach((date) => {
      if (datePart != "") {
        datePart += ", ";
      }
      datePart += localHumanMadeObjectHelpers.trimAndStripCommas(date);
    });
  }
  datePart = datePart.trim();

  if (title_.endsWith(datePart)) {
    return componentIdPart + title_;
  }

  if (title.endsWith(',"') || title.endsWith(",'")) {
    return componentIdPart + title_ + " " + datePart;
  }
  return componentIdPart + title_ + ", " + datePart;
}

/**
 * Checks whether the Information Object is an Institutional Collection or not (Special Collection)
 * @param {Object} obj - the JSON-LD object for an InformationObject
 *
 * @returns {boolean}
 */
export function isInstitutionalCollection(obj) {
  if (
    getClassifiedAs(obj, internal.INSTITUTIONAL_COLLECTION).length > 0 ||
    getClassifiedAs(obj, internal.INSTITUTIONAL_COLLECTION_ALT).length > 0
  ) {
    return true;
  }
  return false;
}

/**
 * Gets the the Information Object collection repository
 * @param {Object} obj - the JSON-LD object for an InformationObject
 *
 * @returns {string}
 */
export function getRepository(obj) {
  if (isInstitutionalCollection(obj)) {
    return "institutional-archives";
  }
  return "special-collections";
}

/**
 * Gets the Media links for the JSON-LD  Object
 * @param {object} obj - the InformationObject
 * @returns {array} a list of links for the object
 */
export function getRelatedMediaLinks(obj) {
  if (obj == undefined) {
    return [];
  }

  let subjects = normalizeFieldToArray(obj, SUBJECT_OF);
  let links = [];
  subjects.forEach((subject) => {
    let accessPoints = normalizeFieldToArray(subject, ACCESS_POINT);
    let accessPoint = accessPoints[0];
    if (accessPoints.length == 0) {
      return;
    }
    let url = accessPoint.id;
    // if it's a webpage && and it goes to a handle (collection), don't include it
    if (
      getClassifiedAs(subject, aat.WEB_PAGE).length > 0 &&
      url.includes("hdl.handle.net")
    ) {
      return;
    }
    let name = "link";
    try {
      name = getEntityName(subject);
    } catch (e) {
      console.warn(e);
    }
    let fullNamesWithDates = getAssignedBy(subject, IDENTIFIED_BY);
    if (fullNamesWithDates.length > 0) {
      name = getValueOrContent(fullNamesWithDates[0]);
    }

    links.push({ href: url, name: name });
  });
  return links;
}

/**
 * Gets the Catalog Record Link for a collection
 *
 * @param {object} collection - the InformationObject
 *
 * @returns {string} a URL for the catalog link
 */
export function getCatalogRecord(collection) {
  if (collection == undefined) {
    return [];
  }

  let subjects = normalizeFieldToArray(collection, SUBJECT_OF);
  let url = undefined;
  subjects.forEach((subject) => {
    let accessPoints = normalizeFieldToArray(subject, ACCESS_POINT);
    if (accessPoints.length > 0 && url == url) {
      url = accessPoints[0].id;
    }
  });

  return url;
}

/**
 * Sort nested arrays recursively in place using the children key
 *
 * @param {object} - object with an array as key value
 * @param {string} - optional key
 */
export function deepSort(parent, key = "children") {
  if (parent[key] && Array.isArray(parent[key])) {
    parent[key].sort((a, b) => a.order - b.order);
    parent[key].forEach((child) => deepSort(child));
  }
}

/**
 * gets the anchor Id
 *
 * @param {string} id
 *
 * @returns {string}
 */
export function getAnchorId(id) {
  return "id-" + id.replace("/", "-");
}

/**
 * Gets the sort title for an object
 *
 * @param {object} object
 *
 * @returns {string}
 */
export function getSortTitle(object) {
  let title = getValueByClassification(object[IDENTIFIED_BY], aat.SORT_TITLE);
  if (title == undefined || title.trim().length == 0) {
    title = getName(object);
  }
  if (title.trim().endsWith(",")) {
    title = title.substring(0, title.lastIndexOf(",")).trim();
  }
  return title.toLowerCase();
}

/**
 * If there aren't primary creators, append a synthetic unknown creator
 *
 * @param {array} primaryCreator - array of creator objects
 *
 * @returns {object} array of creator objects
 */
export function getUnknownCreator() {
  return {
    entity_type: "Person",
    id: "person/UNKNOWN",
    primary_name: "UNKNOWN",
    "primary_name--text": "UNKNOWN",
    type: "Actor"
  };
}

/**
 * Gets the Physical Characteristics and Technical Requirements
 *
 * @param {object} object - the InformationObject
 * @param {object} separateValues - whether to provide UI hints about separating values; default is false to support the indexer
 *
 * @returns {array,string}
 */
export function getPhysicalTechnicalInformation(
  object,
  separateValues = false
) {
  return getReferredToByValueByClassification(
    object,
    internal.PHYSICAL_TECHNICAL,
    separateValues
  );
}

/**
 * Takes an object tree and pads the tree's contents in an array so we can sort it
 *
 * @param {object} tree - an indexer object tree
 * @param {number} length - the length
 *
 * @returns {array}
 */
export function extractTreeOrder(tree, length = 6) {
  let treeOrder = [];
  if (tree == undefined || typeof tree !== "object") {
    return [];
  }

  Object.keys(tree)
    .sort()
    .reverse()
    .forEach((level) => {
      const item = tree[level];
      let order = getInformationObjectOrder(item);
      if (item.type == "HumanMadeObject") {
        order = localHumanMadeObjectHelpers.getObjectOrder(item);
      }
      treeOrder.push(String(order).padStart(length, "0"));
    });
  return treeOrder;
}

/**
 * Returns an object with relators as keys and actors as values
 *
 * @param {array} actors - array of creator route objects (with roles)
 *
 * @returns {object}
 *
 * {
 *    'relator.ctb': [ { ...creator route }]
 * }
 */
export function getMetadataActorsByRole(actors) {
  let roles = {};
  if (Array.isArray(actors) == false) {
    return [];
  }
  for (const actor of actors) {
    if (actor && actor.relators) {
      actor.relators.forEach((e) => {
        roles[e] = (roles[e] || []).concat([actor]);
      });
      delete actor["relators"];
    }
  }

  delete roles[aat.PREFERRED_TERMS];
  roles = Object.entries(roles).map((e) => {
    let key = ["relators." + e[0].split("/").pop()];
    // sort actors
    let value = e[1].sort((a, b) => (a.name > b.name ? 1 : -1));
    // when sending these out as objects to render in vue, we want to remove the relator code
    return [key, value];
  });
  // sort roles
  return Object.fromEntries(roles.sort());
}

/**
 * Gets actors with roles from activities within
 * embedded aggregate human made objects
 *
 * @param {object} object - The JSON-LD for InformationObject
 * @param {string} property - property key for activity to check for assigned attributed roles
 * @param {string} activityProperty - key on activity to check for assigned attributed roles
 *
 * @return {array} actors with assigned LOC relator roles from all embedded hmos
 */
export function getReferredToActorsWithRoles(
  object,
  property,
  activityProperty
) {
  let actors = [];
  const refers_to = getRefersTo(object);

  refers_to.forEach((obj) => {
    let args = [obj, property, activityProperty].filter((e) => e);
    actors = actors.concat(
      localHumanMadeObjectHelpers.getActorsWithRoles(...args)
    );
  });
  return actors;
}

/**
 * gets the list of set names
 *
 * @param {object} object
 *
 * @returns {array} - a list of Set names
 */
export function getSetNames(object) {
  const names = normalizeFieldToArray(object, "member_of")
    .map((set) => getName(set))
    .filter((name) => name != undefined);
  return names;
}

/**
 * Gets the published env (staging/prod) status of an LOD record
 *
 * @param {object} object
 *
 * @returns {boolean} - whether the record is published only in staging (true)
 */
export function getStagingPublishStatus(object) {
  if (!object[CREATED_BY]) {
    return true;
  }
  let complete = getClassifiedAs(
    object[CREATED_BY],
    internal.FINDING_AID_STATUS_COMPLETE
  );
  if (complete.length) {
    return false;
  }
  return true;
}
